import React, { useState, useEffect, useCallback } from "react";
import { fetchCategoryAttributes } from "../api/categoryService";
import { FiInfo } from "react-icons/fi";

function AttributeForm({
  data,
  setData,
  selectedCategory,
  validateRequired = false,
  onRequiredFieldsValidChange, // Yeni prop ekleyelim
}) {
  const [attributes, setAttributes] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [areRequiredFieldsValid, setAreRequiredFieldsValid] = useState(false);

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryAttributes(selectedCategory.slug)
        .then((data) => {
          setAttributes(data);
        })
        .catch((error) => {
          console.error("Error fetching attributes:", error);
        });
    }
  }, [selectedCategory]);

  // Zorunlu alanları kontrol et ve dön
  const validateRequiredFields = useCallback(() => {
    if (!attributes || attributes.length === 0) return true;

    const requiredAttrs = attributes.filter((attr) => attr.required);
    if (requiredAttrs.length === 0) return true; // Zorunlu alan yoksa geçerli

    let isValid = true;
    const errors = {};

    requiredAttrs.forEach((attr) => {
      const attrValue = data.attributes.find((a) => a.id === attr.id)?.value;
      // Boolean değerler için özel kontrol
      if (attr.data_type === "boolean" && attrValue === false) {
        // Boolean için false değeri geçerli
      } else if (!attrValue && attrValue !== 0) {
        errors[attr.id] = "Bu alan zorunludur";
        isValid = false;
      }
    });

    setValidationErrors(errors);
    setAreRequiredFieldsValid(isValid);

    // Üst bileşene geçerlilik durumunu bildir
    if (onRequiredFieldsValidChange) {
      onRequiredFieldsValidChange(isValid);
    }

    return isValid;
  }, [attributes, data.attributes, onRequiredFieldsValidChange]);

  // Her attribute değişikliğinde ve component mount olduğunda zorunlu alanları kontrol et
  useEffect(() => {
    validateRequiredFields();
  }, [validateRequiredFields, data.attributes]);

  // Component dışından validate isteği geldiğinde
  useEffect(() => {
    if (validateRequired) {
      validateRequiredFields();
    }
  }, [validateRequired, validateRequiredFields]);

  // handleAttributeChange fonksiyonunu güncelle
  const handleAttributeChange = (e, attribute) => {
    // Değer belirleme (input tipine bağlı olarak)
    let value;
    if (e.target.type === "checkbox") {
      value = e.target.checked;
    } else {
      value = e.target.value;
    }

    // Değişen değeri konsolda göster (debugging için)
    console.log(`Attribute değişti: ${attribute.name}, yeni değer:`, value);

    // Değer değiştiğinde validasyon hatasını temizle
    if (value !== undefined && value !== "") {
      setValidationErrors((prev) => ({
        ...prev,
        [attribute.id]: null,
      }));
    }

    // Verileri güncelle
    setData((prev) => {
      const newAttributes = [
        ...prev.attributes.filter((attr) => attr.id !== attribute.id),
        {
          id: attribute.id,
          name: attribute.name,
          display_name: attribute.display_name,
          value: value,
          required: attribute.required,
        },
      ];

      console.log("Güncellenmiş attributes:", newAttributes);

      return {
        ...prev,
        attributes: newAttributes,
      };
    });
  };

  return (
    <div className="p-6 bg-white shadow-md rounded-lg">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {attributes.map((attribute) => (
          <div key={attribute.id} className="flex flex-col">
            <label
              htmlFor={attribute.slug}
              className="text-sm font-medium text-gray-700 flex items-center"
            >
              {attribute.display_name}
              {attribute.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
              {attribute.description && (
                <span
                  className="ml-2 text-gray-500"
                  title={attribute.description}
                >
                  <FiInfo />
                </span>
              )}
            </label>

            {attribute.filter_type === "range" ||
            attribute.filter_type === "date" ? (
              <>
                <input
                  id={attribute.slug}
                  type={attribute.data_type}
                  value={
                    data.attributes.find((attr) => attr.id === attribute.id)
                      ?.value || ""
                  }
                  onChange={(e) => handleAttributeChange(e, attribute)}
                  className={`mt-2 p-2 border ${
                    validationErrors[attribute.id]
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  } rounded-lg focus:ring-2 focus:outline-none`}
                />
                {validationErrors[attribute.id] && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors[attribute.id]}
                  </p>
                )}
              </>
            ) : attribute.data_type === "boolean" ? (
              <div className="flex items-center mt-2">
                <input
                  id={attribute.slug}
                  type="checkbox"
                  checked={
                    data.attributes.find((attr) => attr.id === attribute.id)
                      ?.value || false
                  }
                  onChange={(e) => handleAttributeChange(e, attribute)}
                  className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor={attribute.slug} className="ml-2 text-gray-700">
                  {attribute.display_name}
                </label>
              </div>
            ) : attribute.filter_type === "choice" ? (
              <>
                <select
                  id={attribute.slug}
                  value={
                    data.attributes.find((attr) => attr.id === attribute.id)
                      ?.value || ""
                  }
                  onChange={(e) => handleAttributeChange(e, attribute)}
                  className={`mt-2 p-2 border ${
                    validationErrors[attribute.id]
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  } rounded-lg focus:ring-2 focus:outline-none`}
                >
                  <option value="">Seçin</option>
                  {attribute?.choices?.map((choice, index) => (
                    <option key={index} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
                {validationErrors[attribute.id] && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors[attribute.id]}
                  </p>
                )}
              </>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AttributeForm;
